import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
  getCustomerProductReview,
  getPublicProductReviewSnapshot,
  listAdminReviewedProductOptions,
  listAdminProductReviews,
  listReviewableOrderItems,
  removeProductReviewResponse,
  setProductReviewVisibility,
  submitProductReview,
  updateCustomerProductReview,
  upsertProductReviewResponse,
} from '../services/review.service';
import type { SupabaseClient } from '../supabase/client';
import type {
  AdminReviewedProductOption,
  AdminReviewFilters,
  AdminReviewsPage,
  CustomerProductReview,
  PublicProductReviewSnapshot,
  ReviewableOrderItem,
  RemoveReviewResponseInput,
  SetReviewVisibilityInput,
  SubmitProductReviewInput,
  SubmitProductReviewResult,
  UpdateCustomerProductReviewInput,
  UpdateCustomerProductReviewResult,
  UpsertReviewResponseInput,
} from '../types/review';
import { customerQueryKeys } from './customer-query-keys';

export const reviewKeys = {
  all: ['reviews'] as const,
  reviewable: (userId: string | null | undefined, orderId: string | null) =>
    customerQueryKeys.reviewableOrderItems(userId, orderId),
  customerProduct: (userId: string | null | undefined, storeSlug: string, productId: string) =>
    customerQueryKeys.customerProductReview(userId, storeSlug, productId),
  publicProduct: (storeSlug: string, productId: string, highlightedReviewId?: string | null) =>
    highlightedReviewId
      ? (['reviews', 'public-product', storeSlug, productId, highlightedReviewId] as const)
      : (['reviews', 'public-product', storeSlug, productId] as const),
  adminList: (
    storeId: string,
    productId: string | null,
    visibility: string,
    limit: number,
    offset: number,
  ) => ['reviews', 'admin', storeId, productId ?? 'all', visibility, limit, offset] as const,
  adminReviewedProducts: (storeId: string, search: string, limit: number, offset: number) =>
    ['reviews', 'admin-reviewed-products', storeId, search, limit, offset] as const,
};

function normalizeAdminLimit(value: number | undefined): number {
  return Math.min(Math.max(Number.isFinite(value ?? 20) ? Math.trunc(value ?? 20) : 20, 1), 50);
}

function normalizeAdminOffset(value: number | undefined): number {
  return Math.max(Number.isFinite(value ?? 0) ? Math.trunc(value ?? 0) : 0, 0);
}

export function useReviewableOrderItems(args: {
  readonly client: SupabaseClient;
  readonly userId?: string | null;
  readonly orderId?: string | null;
  readonly enabled?: boolean;
  readonly limit?: number;
}): UseQueryResult<readonly ReviewableOrderItem[]> {
  const userId = args.userId ?? null;
  const orderId = args.orderId ?? null;
  return useQuery({
    queryKey: reviewKeys.reviewable(userId, orderId),
    enabled: (args.enabled ?? true) && !!userId && !!orderId,
    queryFn: () =>
      listReviewableOrderItems(args.client, {
        orderId,
        limit: args.limit,
      }),
    staleTime: 30_000,
  });
}

export function useSubmitProductReview(
  client: SupabaseClient,
  scope?: { readonly storeSlug?: string | null; readonly productId?: string | null },
): UseMutationResult<SubmitProductReviewResult, unknown, SubmitProductReviewInput> {
  const queryClient = useQueryClient();
  const storeSlug = scope?.storeSlug?.trim() ?? '';
  const productId = scope?.productId?.trim() ?? '';
  return useMutation({
    mutationFn: (input) => submitProductReview(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
      void queryClient.invalidateQueries({ queryKey: customerQueryKeys.reviewsAll });
      if (storeSlug && productId) {
        void queryClient.invalidateQueries({
          queryKey: reviewKeys.publicProduct(storeSlug, productId),
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['store-home-merchandising'] });
      void queryClient.invalidateQueries({ queryKey: customerQueryKeys.ordersAll });
    },
  });
}

export function useCustomerProductReview(args: {
  readonly client: SupabaseClient;
  readonly userId?: string | null;
  readonly storeSlug: string;
  readonly productId: string;
}): UseQueryResult<CustomerProductReview | null> {
  const userId = args.userId ?? null;
  const storeSlug = args.storeSlug.trim();
  return useQuery({
    queryKey: reviewKeys.customerProduct(userId, storeSlug, args.productId),
    enabled: !!userId && !!storeSlug && !!args.productId,
    queryFn: () =>
      getCustomerProductReview(args.client, {
        storeSlug,
        productId: args.productId,
      }),
    staleTime: 30_000,
  });
}

export function useUpdateCustomerProductReview(
  client: SupabaseClient,
  scope: { readonly userId: string | null; readonly storeSlug: string; readonly productId: string },
): UseMutationResult<UpdateCustomerProductReviewResult, unknown, UpdateCustomerProductReviewInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => updateCustomerProductReview(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
      void queryClient.invalidateQueries({ queryKey: customerQueryKeys.reviewsAll });
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.customerProduct(scope.userId, scope.storeSlug.trim(), scope.productId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.publicProduct(scope.storeSlug.trim(), scope.productId),
      });
      void queryClient.invalidateQueries({ queryKey: ['store-home-merchandising'] });
    },
  });
}

export function usePublicProductReviewSnapshot(args: {
  readonly client: SupabaseClient;
  readonly storeSlug: string;
  readonly productId: string;
  readonly limit?: number;
  readonly highlightedReviewId?: string | null;
}): UseQueryResult<PublicProductReviewSnapshot> {
  const storeSlug = args.storeSlug.trim();
  const highlightedReviewId = args.highlightedReviewId ?? undefined;
  return useQuery({
    queryKey: reviewKeys.publicProduct(storeSlug, args.productId, highlightedReviewId),
    enabled: !!storeSlug && !!args.productId,
    queryFn: () =>
      getPublicProductReviewSnapshot(args.client, {
        storeSlug,
        productId: args.productId,
        limit: args.limit,
        highlightedReviewId,
      }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

export function useAdminReviewedProductOptions(args: {
  readonly client: SupabaseClient;
  readonly storeId: string | null;
  readonly search?: string | null;
  readonly limit?: number;
  readonly offset?: number;
}): UseQueryResult<readonly AdminReviewedProductOption[]> {
  const search = args.search?.trim() ?? '';
  const limit = Math.min(
    Math.max(Number.isFinite(args.limit ?? 100) ? Math.trunc(args.limit ?? 100) : 100, 1),
    100,
  );
  const offset = normalizeAdminOffset(args.offset);
  return useQuery({
    queryKey: args.storeId
      ? reviewKeys.adminReviewedProducts(args.storeId, search, limit, offset)
      : reviewKeys.all,
    enabled: !!args.storeId,
    queryFn: () =>
      listAdminReviewedProductOptions(args.client, {
        storeId: args.storeId ?? '',
        search,
        limit,
        offset,
      }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useAdminReviews(args: {
  readonly client: SupabaseClient;
  readonly filters: AdminReviewFilters | null;
}): UseQueryResult<AdminReviewsPage> {
  const filters = args.filters;
  return useQuery({
    queryKey: filters
      ? reviewKeys.adminList(
          filters.storeId,
          filters.productId ?? null,
          filters.visibility ?? 'all',
          normalizeAdminLimit(filters.limit),
          normalizeAdminOffset(filters.offset),
        )
      : reviewKeys.all,
    enabled: !!filters,
    queryFn: () => {
      if (!filters) throw new Error('useAdminReviews: filters are required when query is enabled');
      return listAdminProductReviews(args.client, filters);
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useUpsertReviewResponse(
  client: SupabaseClient,
): UseMutationResult<void, unknown, UpsertReviewResponseInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => upsertProductReviewResponse(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['store-home-merchandising'] });
    },
  });
}

export function useRemoveReviewResponse(
  client: SupabaseClient,
): UseMutationResult<void, unknown, RemoveReviewResponseInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => removeProductReviewResponse(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['store-home-merchandising'] });
    },
  });
}

export function useSetReviewVisibility(
  client: SupabaseClient,
): UseMutationResult<void, unknown, SetReviewVisibilityInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => setProductReviewVisibility(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['store-home-merchandising'] });
    },
  });
}
